import { useFetch } from "@/hooks/useFetch";
import { trackCustomEvent } from "@/lib/newrelic";
import { mapApiResponseToOffers } from "@/lib/offers";
import { buildTrackingContext } from "@/lib/tracking/apiTracking";
import { fetchInternetOffers } from "@/services/api/internetOffers";
import type { InternetOffersApiResponse, Offer } from "@/types/offers";
import { useEffect, useMemo } from "react";

export interface UseInternetOffersState {
  offers: Offer[] | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook to fetch and manage internet offers from the API
 *
 * @param salesSessionId - The RC.SP value from the SC cookie
 * @returns Object containing offers array (or null on failure), loading state, and error
 *
 * @example
 * ```tsx
 * const { offers, isLoading, error } = useInternetOffers(salesSessionId);
 * // If offers is null, show regular non-localized cards instead
 * ```
 */
export function useInternetOffers(
  salesSessionId: string | null | undefined
): UseInternetOffersState {
  const shouldFetch = Boolean(salesSessionId);
  const trackingContext = useMemo(
    () =>
      buildTrackingContext({
        endpoint: "internetOffers",
        salesSessionId: salesSessionId,
      }),
    [salesSessionId]
  );

  const fetcher = useMemo(
    () => async (signal: AbortSignal) => {
      trackCustomEvent("requestedOffers", trackingContext);

      const result = await fetchInternetOffers({
        salesSessionId: salesSessionId || "",
        signal,
      });

      if (!result.error && result.data) {
        const offersFromApi = mapApiResponseToOffers(result.data);
        if (offersFromApi.length > 0) {
          trackCustomEvent("returnedOffers", {
            ...trackingContext,
            offerCount: offersFromApi.length.toString(),
          });
        } else {
          trackCustomEvent("returnedEmptyOffers", trackingContext);
        }
      }

      return result;
    },
    [salesSessionId, trackingContext]
  );

  const fetchOptions = useMemo(
    () => ({
      enabled: shouldFetch,
      trackingContext,
      errorSource: "internetOffers",
      errorCustomAttributes: { offersError: "Failed to fetch internet offers" },
    }),
    [shouldFetch, trackingContext]
  );

  const {
    data: apiResponse,
    isLoading,
    error,
  } = useFetch<InternetOffersApiResponse>(fetcher, fetchOptions);

  const offers = useMemo(() => {
    if (!apiResponse) return null;
    const transformed = mapApiResponseToOffers(apiResponse) ?? [];
    return transformed.length > 0 ? transformed : null;
  }, [apiResponse]);

  useEffect(() => {
    if (!shouldFetch) return;
    if (isLoading) return;

    trackCustomEvent("internetOffersTracked", {
      ...trackingContext,
      offersCount: offers?.length?.toString() || "0",
    });
  }, [shouldFetch, isLoading, trackingContext, offers]);

  return {
    offers,
    isLoading,
    error,
  };
}
